const SERVICE_WORKER_PATH = "/TickbirdServiceWorker.js";
const ROOT_SCOPE = "/";

let registrationPromise: Promise<ServiceWorkerRegistration> | undefined;

function supportsServiceWorker(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function serviceWorkerUrl(): string {
  const version = encodeURIComponent(import.meta.env.VITE_APP_BUILD_VERSION);
  return `${SERVICE_WORKER_PATH}?v=${version}`;
}

function isCurrentTickbirdWorker(worker: ServiceWorker | null): worker is ServiceWorker {
  if (!worker) {
    return false;
  }

  return new URL(worker.scriptURL).href === new URL(serviceWorkerUrl(), window.location.origin).href;
}

function waitForActiveTickbirdWorker(registration: ServiceWorkerRegistration): Promise<void> {
  return new Promise((resolve, reject) => {
    const observedWorkers = new Set<ServiceWorker>();
    const timeout = window.setTimeout(() => finish(new Error("Tickbird Service Worker activation timed out.")), 15_000);

    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      registration.removeEventListener("updatefound", inspect);
      navigator.serviceWorker.removeEventListener("controllerchange", inspect);
      for (const worker of observedWorkers) {
        worker.removeEventListener("statechange", inspect);
      }

      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    const inspect = () => {
      const replacement = [registration.waiting, registration.installing].find(isCurrentTickbirdWorker) ?? null;
      const active = registration.active;

      // If a current-version replacement exists, wait for that exact worker instead of
      // accepting an older active worker which happens to share the same pathname.
      if (!replacement && isCurrentTickbirdWorker(active) && active.state === "activated") {
        finish();
        return;
      }

      for (const candidate of [replacement, active]) {
        if (!isCurrentTickbirdWorker(candidate)) {
          continue;
        }
        if (candidate.state === "redundant") {
          finish(new Error("Tickbird Service Worker installation failed."));
          return;
        }
        if (!observedWorkers.has(candidate)) {
          observedWorkers.add(candidate);
          candidate.addEventListener("statechange", inspect);
        }
      }
    };

    registration.addEventListener("updatefound", inspect);
    navigator.serviceWorker.addEventListener("controllerchange", inspect);
    inspect();
  });
}

export function registerTickbirdServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!supportsServiceWorker()) {
    return Promise.reject(new Error("当前浏览器不支持 Service Worker。"));
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(serviceWorkerUrl(), {
        scope: ROOT_SCOPE,
        updateViaCache: "none",
      })
      .then(async (registration) => {
        await waitForActiveTickbirdWorker(registration);
        return registration;
      })
      .catch((error: unknown) => {
        registrationPromise = undefined;
        throw error;
      });
  }

  return registrationPromise;
}

export function getTickbirdServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!supportsServiceWorker()) {
    return Promise.resolve(undefined);
  }

  return navigator.serviceWorker.getRegistration(new URL(ROOT_SCOPE, window.location.origin));
}
